import { PermutationCommand } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { z } from "@eisox/zod";

import { m } from "~/paraglide/messages";

export const isBoilerPermutationIndependent = (pumpsPermutationDay?: number, pumpsPermutationHour?: number): boolean =>
  pumpsPermutationDay !== undefined && pumpsPermutationHour !== undefined;

const LOSS_TEMP_DELTA_TEMPERATURE = {
  MIN: 0,
  MAX: 100,
} as const;

export const SETPOINT_TEMPERATURE = {
  MIN: 0,
  MAX: 100,
  STEP: 1,
} as const;

interface SetpointTemperatureBounds {
  lowSetpointTemperatureBounds: TemperatureBounds;
  highSetpointTemperatureBounds: TemperatureBounds;
}

interface TemperatureBounds {
  min: number;
  max: number;
}

const createBounds = ({ min, max }: TemperatureBounds): TemperatureBounds => ({
  min,
  max: Math.max(min, max),
});

export const getLowSetpointTemperatureBounds = (highSetpointTemperature?: number): TemperatureBounds => {
  const boundedMaxTemperature =
    highSetpointTemperature === undefined
      ? SETPOINT_TEMPERATURE.MAX
      : Math.min(SETPOINT_TEMPERATURE.MAX, highSetpointTemperature - SETPOINT_TEMPERATURE.STEP);

  return createBounds({
    min: SETPOINT_TEMPERATURE.MIN,
    max: boundedMaxTemperature,
  });
};

export const getHighSetpointTemperatureBounds = (lowSetpointTemperature?: number): TemperatureBounds => {
  const boundedMinTemperature =
    lowSetpointTemperature === undefined
      ? SETPOINT_TEMPERATURE.MIN
      : Math.min(SETPOINT_TEMPERATURE.MAX, lowSetpointTemperature + SETPOINT_TEMPERATURE.STEP);

  return createBounds({
    min: boundedMinTemperature,
    max: SETPOINT_TEMPERATURE.MAX,
  });
};

export const getSetpointTemperatureBounds = ({
  lowSetpointTemperature,
  highSetpointTemperature,
}: {
  lowSetpointTemperature?: number;
  highSetpointTemperature?: number;
}): SetpointTemperatureBounds => ({
  lowSetpointTemperatureBounds: getLowSetpointTemperatureBounds(highSetpointTemperature),
  highSetpointTemperatureBounds: getHighSetpointTemperatureBounds(lowSetpointTemperature),
});

export const boilerSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  group: z.number().min(1).optional(),
  priorityForcing: z.number().min(1).optional(),
  priority: z.number().optional(),
});

export const getBoilersGroups = (boilers: BoilerType[] = []): number[] =>
  Array.from(new Set(boilers.map(b => b.group).filter((g): g is number => typeof g === "number")));

export const schema = (isBoilerGroupAvailable: boolean) =>
  z
    .object({
      lossTempDelta: z.number().min(LOSS_TEMP_DELTA_TEMPERATURE.MIN).max(LOSS_TEMP_DELTA_TEMPERATURE.MAX).optional(),
      lowSetpointTemperature: z.number().min(SETPOINT_TEMPERATURE.MIN).max(SETPOINT_TEMPERATURE.MAX).optional(),
      highSetpointTemperature: z.number().min(SETPOINT_TEMPERATURE.MIN).max(SETPOINT_TEMPERATURE.MAX).optional(),
      permutationCmd: z.enum(PermutationCommand).optional(),
      permutationDay: z.number().min(1).max(7).optional(),
      permutationHour: z.number().min(0).max(23).optional(),
      groupCount: z.number().optional(),
      masterGroup: z.number().min(0).optional().default(0),
      boilers: z.array(boilerSchema).optional().default([]),
    })
    .superRefine((data, ctx) => {
      if (data.permutationCmd === PermutationCommand.AUTO && isBoilerGroupAvailable) {
        const boilers = data.boilers;
        const groups = getBoilersGroups(boilers);

        if (data.groupCount === undefined) {
          ctx.addIssue({
            code: "custom",
            path: ["groupCount"],
            message: m.cascade_dialog_error_group_count_required(),
          });
        }

        if (data.masterGroup !== 0 && !groups.includes(data.masterGroup)) {
          ctx.addIssue({
            code: "custom",
            path: ["masterGroup"],
            message: m.cascade_dialog_error_master_group(),
          });
        }

        if (data.groupCount !== undefined && groups.length !== data.groupCount) {
          ctx.addIssue({
            code: "custom",
            path: ["groupCount"],
            message: m.cascade_dialog_error_group_count_mismatch(),
          });
        }

        const sortedGroups = [...groups].sort((a, b) => a - b);
        const hasGap = sortedGroups.some((group, index) => group !== index + 1);
        if (hasGap) {
          ctx.addIssue({
            code: "custom",
            path: ["boilers"],
            message: m.cascade_dialog_error_boilers_gap(),
          });
        }
      } else if (data.permutationCmd === PermutationCommand.MANUAL) {
        data.boilers.forEach((boiler, index) => {
          if (boiler.priorityForcing === undefined) {
            ctx.addIssue({
              code: "custom",
              path: ["boilers", index, "priorityForcing"],
              message: m.cascade_dialog_error_priority_forcing(),
            });
          }
        });
      }
    });

export type SchemaType = z.infer<ReturnType<typeof schema>>;
export type BoilerType = z.infer<typeof boilerSchema>;

export const isBoilerGroupAvailable = (group?: number, boilers: BoilerType[] = []): boolean =>
  boilers.some(b => b.group !== undefined) && group !== undefined;

export const getNextDate = (targetDay: number, targetHour: number): dayjs.Dayjs => {
  const now = dayjs();
  let nextDate = now.isoWeekday(targetDay).hour(targetHour).minute(0).second(0).millisecond(0);
  if (nextDate.isBefore(now)) nextDate = nextDate.add(1, "week");
  return nextDate;
};

export const getBoilerNamesByGroup = (group: number, boilers?: BoilerType[]): string | undefined =>
  boilers
    ?.filter(b => b.group === group)
    .map(b => b.name ?? "-")
    .join(", ");
